/*
 * The installed-app layer shared by every Hume app (no dependencies; pairs with css/pwa.css). Load it on
 * every page, after the tab bar's markup if possible (it also waits for DOMContentLoaded):
 *
 *   <link rel="manifest" href="/manifest.webmanifest">
 *   <meta name="service-worker" content="/sw.js?v=<build>">   registered at scope "/"
 *   <script src="…/assets/js/pwa.js" defer></script>            (or `import ".../pwa.js"` in a bundle)
 *
 *   Display mode  <html data-display-mode="browser|standalone|window-controls-overlay">
 *   Updates       a new service worker waits until the person picks "Reload" on the update banner;
 *                 updates are checked when the app comes back to the foreground, and hourly.
 *   Install       [data-pwa-install] elements (hidden until installing is possible) start the browser's
 *                 install prompt; on iPhone and iPad they explain Share → Add to Home Screen.
 *   Theme color   <meta name="theme-color"> follows the page's background (light / dark / tenant), so the
 *                 status bar and the desktop title bar match the app.
 *   Keyboard      <html data-keyboard> while the on-screen keyboard is up (the tab bar steps aside).
 *   Tab bar       .tabbar-action[data-tabbar-action="auto"] takes the page's main action: the element
 *                 with [data-primary-action], else the first filled .btn in .page-actions. The original
 *                 is marked [data-tabbar-source] (hidden on phones) and clicked through. A slot marked
 *                 [data-tabbar-fallback] keeps its own markup (say, a Search tab) on pages without a main
 *                 action, so the bar keeps the same slots on every page.
 *
 * window.humePwa: install() · canInstall() · applyUpdate() · checkForUpdate() · refreshTabbarAction() · isStandalone()
 * Events on window: "pwa:installable", "pwa:installed", "pwa:update" (detail: the registration).
 */
(function () {
  "use strict";

  if (window.humePwa) return;

  var root = document.documentElement;
  var MODES = ["window-controls-overlay", "standalone", "minimal-ui", "fullscreen"];
  var deferredPrompt = null;
  var registration = null;
  var reloading = false;
  var banner = null;

  function displayMode() {
    for (var i = 0; i < MODES.length; i++) {
      if (window.matchMedia("(display-mode: " + MODES[i] + ")").matches) return MODES[i];
    }
    return navigator.standalone ? "standalone" : "browser";
  }

  function isStandalone() {
    return displayMode() !== "browser";
  }

  function trackDisplayMode() {
    root.dataset.displayMode = displayMode();
    MODES.forEach(function (mode) {
      var query = window.matchMedia("(display-mode: " + mode + ")");
      var update = function () {
        root.dataset.displayMode = displayMode();
      };
      if (query.addEventListener) query.addEventListener("change", update);
    });
  }

  function emit(name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  /* --- Banner -------------------------------------------------------------------------------- */

  function closeBanner() {
    if (banner) banner.remove();
    banner = null;
  }

  /** A small card above the tab bar: a title, a line of text and up to two buttons. */
  function showBanner(options) {
    closeBanner();
    banner = document.createElement("div");
    banner.className = "pwa-banner";
    banner.setAttribute("role", "status");
    var text = document.createElement("div");
    text.className = "pwa-banner-text";
    var title = document.createElement("strong");
    title.textContent = options.title;
    text.appendChild(title);
    if (options.text) text.appendChild(document.createTextNode(options.text));
    banner.appendChild(text);
    var actions = document.createElement("div");
    actions.className = "pwa-banner-actions";
    options.actions.forEach(function (action) {
      var button = document.createElement("button");
      button.type = "button";
      button.textContent = action.label;
      if (action.primary) button.dataset.primary = "";
      button.addEventListener("click", function () {
        closeBanner();
        if (action.run) action.run();
      });
      actions.appendChild(button);
    });
    banner.appendChild(actions);
    document.body.appendChild(banner);
  }

  /* --- Service worker and updates ------------------------------------------------------------- */

  function promptForUpdate(reg) {
    if (!reg.waiting || !navigator.serviceWorker.controller) return;
    emit("pwa:update", reg);
    showBanner({
      title: "A new version is ready",
      text: "Reload to start using it.",
      actions: [
        { label: "Later" },
        { label: "Reload", primary: true, run: applyUpdate },
      ],
    });
  }

  function applyUpdate() {
    if (!registration || !registration.waiting) return window.location.reload();
    reloading = true;
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }

  function checkForUpdate() {
    if (registration) registration.update().catch(function () {});
  }

  function registerServiceWorker() {
    var meta = document.querySelector('meta[name="service-worker"]');
    if (!meta || !("serviceWorker" in navigator) || !window.isSecureContext) return;
    navigator.serviceWorker
      .register(meta.content, { scope: "/", updateViaCache: "none" })
      .then(function (reg) {
        registration = reg;
        promptForUpdate(reg);
        reg.addEventListener("updatefound", function () {
          var worker = reg.installing;
          if (!worker) return;
          worker.addEventListener("statechange", function () {
            if (worker.state === "installed") promptForUpdate(reg);
          });
        });
      })
      .catch(function () {
        /* No offline support on this browser; the app works as a web page. */
      });
    navigator.serviceWorker.addEventListener("controllerchange", function () {
      if (reloading) window.location.reload();
    });
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "visible") checkForUpdate();
    });
    window.setInterval(checkForUpdate, 60 * 60 * 1000);
  }

  /* --- Install -------------------------------------------------------------------------------- */

  function isIos() {
    return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  }

  function canInstall() {
    return !!deferredPrompt || (isIos() && !isStandalone() && "serviceWorker" in navigator);
  }

  function setInstallable(on) {
    document.querySelectorAll("[data-pwa-install]").forEach(function (el) {
      el.hidden = !on;
    });
  }

  function install() {
    if (deferredPrompt) {
      var prompt = deferredPrompt;
      deferredPrompt = null;
      prompt.prompt();
      return prompt.userChoice.then(function (choice) {
        if (choice.outcome === "accepted") setInstallable(false);
        return choice.outcome;
      });
    }
    if (isIos() && !isStandalone()) {
      showBanner({
        title: "Install this app",
        text: "Tap Share, then “Add to Home Screen”.",
        actions: [{ label: "Got it", primary: true }],
      });
    }
    return Promise.resolve("dismissed");
  }

  function watchInstall() {
    window.addEventListener("beforeinstallprompt", function (event) {
      event.preventDefault();
      deferredPrompt = event;
      setInstallable(true);
      emit("pwa:installable");
    });
    window.addEventListener("appinstalled", function () {
      deferredPrompt = null;
      setInstallable(false);
      emit("pwa:installed");
    });
    document.addEventListener("click", function (event) {
      var trigger = event.target.closest && event.target.closest("[data-pwa-install]");
      if (!trigger) return;
      event.preventDefault();
      install();
    });
    setInstallable(canInstall());
  }

  /* --- Theme color ---------------------------------------------------------------------------- */

  function syncThemeColor() {
    if (!document.body) return;
    var color = window.getComputedStyle(document.body).backgroundColor;
    if (!color || color === "rgba(0, 0, 0, 0)" || color === "transparent") return;
    var metas = document.querySelectorAll('meta[name="theme-color"]');
    var meta = null;
    metas.forEach(function (m) {
      if (m.hasAttribute("data-pwa")) meta = m;
      else m.remove(); // light/dark variants by media query: the page's own theme toggle wins
    });
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      meta.setAttribute("data-pwa", "");
      document.head.appendChild(meta);
    }
    if (meta.content !== color) meta.content = color;
  }

  function watchThemeColor() {
    syncThemeColor();
    var pending = false;
    var later = function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        syncThemeColor();
      });
    };
    new MutationObserver(later).observe(root, { attributes: true, attributeFilter: ["class", "style", "data-theme"] });
    document.addEventListener("themechange", later);
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", later);
  }

  /* --- On-screen keyboard --------------------------------------------------------------------- */

  function watchKeyboard() {
    var viewport = window.visualViewport;
    if (!viewport) return;
    var editing = function () {
      var el = document.activeElement;
      return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
    };
    var update = function () {
      var open = editing() && window.innerHeight - viewport.height > 120;
      if (open) root.setAttribute("data-keyboard", "");
      else root.removeAttribute("data-keyboard");
    };
    viewport.addEventListener("resize", update);
    document.addEventListener("focusout", function () {
      window.setTimeout(update, 50);
    });
  }

  /* --- The tab bar's page action ---------------------------------------------------------------- */

  var PLUS =
    '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg>';
  var SECONDARY = /\bbtn-(outline|ghost|secondary|link|destructive)\b/;

  function primaryAction() {
    var marked = document.querySelector("[data-primary-action]");
    if (marked) return marked;
    var candidates = document.querySelectorAll(".page-actions .btn, .page-actions button");
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (/\bbtn\b/.test(el.className) && !SECONDARY.test(el.className) && !el.disabled && !el.closest(".dropdown")) return el;
    }
    return null;
  }

  function refreshTabbarAction() {
    document.querySelectorAll('.tabbar-action[data-tabbar-action="auto"]').forEach(function (slot) {
      document.querySelectorAll("[data-tabbar-source]").forEach(function (el) {
        el.removeAttribute("data-tabbar-source");
      });
      if (slot._fallback == null) slot._fallback = slot.hasAttribute("data-tabbar-fallback") ? slot.innerHTML : "";
      slot.textContent = "";
      var source = primaryAction();
      if (!source) {
        slot.innerHTML = slot._fallback;
        slot.hidden = !slot._fallback;
        return;
      }
      var label = (source.getAttribute("aria-label") || source.textContent || "").replace(/\s+/g, " ").trim();
      var button = document.createElement("button");
      button.type = "button";
      button.setAttribute("aria-label", label);
      button.title = label;
      var icon = source.querySelector("svg");
      button.innerHTML = icon ? icon.outerHTML : PLUS;
      button.addEventListener("click", function () {
        source.click();
      });
      source.setAttribute("data-tabbar-source", "");
      slot.appendChild(button);
      slot.hidden = false;
    });
  }

  /* --- Start ---------------------------------------------------------------------------------- */

  function start() {
    trackDisplayMode();
    watchInstall();
    watchThemeColor();
    watchKeyboard();
    refreshTabbarAction();
  }

  window.humePwa = {
    install: install,
    canInstall: canInstall,
    applyUpdate: applyUpdate,
    checkForUpdate: checkForUpdate,
    refreshTabbarAction: refreshTabbarAction,
    isStandalone: isStandalone,
  };
  registerServiceWorker();
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
